import React, { createContext, useContext, useState, useEffect } from 'react';

export interface ThemeColors {
    background: string;
    sidebar: string;
    sidebarText: string;
    header: string;
    card: string;
    text: string;
    textMuted: string;
    primary: string;
    secondary: string;
    border: string;
}

export interface Theme {
    id: string;
    name: string;
    type: 'light' | 'dark';
    colors: ThemeColors;
    backgroundImage?: string; // CSS background property or URL
    sidebarImage?: string; // Optional distinct sidebar background image
}


const classicTheme: Theme = {
    id: 'classic',
    name: 'Pet Smart Monitor Modern',
    type: 'light',
    colors: {
        background: '#f8fafc',
        sidebar: 'rgba(255, 255, 255, 0.95)',
        sidebarText: '#475569',
        header: 'transparent', // Make header transparent to show gradient body
        card: '#ffffff',
        text: '#0f172a',
        textMuted: '#64748b',
        primary: 'linear-gradient(to right, #06b6d4, #d946ef)', // Petaux vibrant gradient
        secondary: '#f1f5f9',
        border: '#f1f5f9',
    },
    backgroundImage: 'linear-gradient(135deg, #e0f2fe 0%, #fdf4ff 50%, #fae8ff 100%)',
};

export const themes: Theme[] = [
    classicTheme,
    {
        id: 'midnight',
        name: 'Midnight City',
        type: 'dark',
        colors: {
            background: '#09090b',
            sidebar: 'rgba(24, 24, 27, 0.95)',
            sidebarText: '#a1a1aa',
            header: 'rgba(9, 9, 11, 0.8)',
            card: 'rgba(39, 39, 42, 0.9)',
            text: '#fafafa',
            textMuted: '#a1a1aa',
            primary: '#6366f1', // Indigo
            secondary: '#27272a',
            border: '#3f3f46',
        },
        backgroundImage: 'radial-gradient(ellipse at bottom, #1e1b4b 0%, #09090b 100%)', // Night sky sim
        sidebarImage: 'linear-gradient(to bottom, #18181b, #000000)',
    }
];

interface ThemeContextType {
    theme: Theme;
    setTheme: (id: string) => void;
    availableThemes: Theme[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [themeId, setThemeId] = useState<string>('classic');

    useEffect(() => {
        const savedTheme = localStorage.getItem('petmonitor-theme');
        if (savedTheme && themes.find(t => t.id === savedTheme)) {
            setThemeId(savedTheme);
        }
    }, []);

    const setTheme = (id: string) => {
        if (themes.find(t => t.id === id)) {
            setThemeId(id);
            localStorage.setItem('petmonitor-theme', id);
        }
    };

    const currentTheme = themes.find(t => t.id === themeId) || classicTheme;

    // Apply CSS variables to root based on theme
    useEffect(() => {
        const root = document.documentElement;

        // Helper to set CSS var
        const setVar = (name: string, value: string) => {
            root.style.setProperty(`--theme-${name}`, value);
        };

        const colors = currentTheme.colors;
        setVar('bg', colors.background);
        setVar('sidebar-bg', colors.sidebar);
        setVar('sidebar-text', colors.sidebarText);
        setVar('header-bg', colors.header);
        setVar('card-bg', colors.card);
        setVar('text', colors.text);
        setVar('text-muted', colors.textMuted);
        setVar('primary', colors.primary);
        setVar('secondary', colors.secondary);
        setVar('border', colors.border);

        if (currentTheme.backgroundImage) {
            setVar('bg-image', currentTheme.backgroundImage);
        } else {
            setVar('bg-image', 'none');
        }

        if (currentTheme.sidebarImage) {
            setVar('sidebar-bg-image', currentTheme.sidebarImage);
        } else {
            setVar('sidebar-bg-image', 'none');
        }

    }, [currentTheme]);

    return (
        <ThemeContext.Provider value={{ theme: currentTheme, setTheme, availableThemes: themes }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context as ThemeContextType;
};
